import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { msalInstance, loginRequest, isMsalConfigured } from '../services/msalConfig';
import { googleLoginUrl, login, register } from '../services/authApi';
import { studentLogin, accessCodeLogin } from '../services/studentAuthApi';
import { getDeviceId } from '../lib/deviceId';
import { useAuthStore } from '../stores/authStore';
import { useFeatureStore } from '../stores/featureStore';
import Button from '../components/ui/Button';

// Microsoft "Segoe" logo squares.
function MsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      <rect x="0" y="0" width="7" height="7" fill="#F25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7FBA00" />
      <rect x="0" y="9" width="7" height="7" fill="#00A4EF" />
      <rect x="9" y="9" width="7" height="7" fill="#FFB900" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29.1 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 18.9 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29.1 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.6-11.3-8.4l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.2 35.7 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}

type Mode = 'signin' | 'register';
type Audience = 'student' | 'staff' | 'access';

// Map a failed email/password request to a friendly inline message.
function messageForAuthError(err: unknown, mode: Mode): string {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401) return 'Incorrect email or password.';
    if (status === 409) return 'An account with that email already exists.';
    if (status === 400) return 'Please check your details and try again.';
  }
  return mode === 'register'
    ? 'Could not create your account. Please try again.'
    : 'Could not sign in. Please try again.';
}

// Map a failed student-ID login to a friendly inline message. Each BP 26
// error code gets its own copy — 423 in particular tells the student when
// they can try again instead of just "locked".
function messageForStudentLoginError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; retry_after_seconds?: number } | undefined;
    if (data?.error === 'account_locked') {
      const mins = Math.ceil((data.retry_after_seconds ?? 60) / 60);
      return `Too many attempts. This account is locked for about ${mins} minute${mins === 1 ? '' : 's'}.`;
    }
    if (data?.error === 'initial_password_expired') {
      return 'Your temporary password has expired. Ask your instructor for a reset code.';
    }
    if (err.response?.status === 401) return 'Incorrect student ID or password.';
    if (err.response?.status === 400) return 'Please check your student ID and password.';
  }
  return 'Could not sign in. Please try again.';
}

export default function LoginPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setAuth = useAuthStore((s) => s.setAuth);

  // Which ways in the platform is currently offering. Both come from
  // /settings/public, through the same service the server gates on, so the
  // page can never offer a door the server will refuse.
  const studentLoginOffered = useFeatureStore(
    (st) => (st.defaults as { student_login_enabled?: boolean }).student_login_enabled === true,
  );

  // Student ID was the default tab (BP 26). It cannot be, once it can be
  // switched off: landing on a hidden tab shows an empty panel.
  const [audience, setAudience] = useState<Audience>('student');

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false); // email/password submit
  const [oauthBusy, setOauthBusy] = useState(false); // Microsoft redirect kickoff
  const [error, setError] = useState<string | null>(null);

  const [studentId, setStudentId] = useState('');
  const [studentPassword, setStudentPassword] = useState('');
  const [studentBusy, setStudentBusy] = useState(false);
  const [studentError, setStudentError] = useState<string | null>(null);

  // Access code — the third tab, offered only when Platform Admin has turned
  // it on. Read from /settings/public, which carries the switch but never the
  // code itself.
  const accessCodeOffered = useFeatureStore(
    (st) => (st.defaults as { access_code_enabled?: boolean }).access_code_enabled === true,
  );
  const [accessCode, setAccessCode] = useState('');
  // Remembered so the same person returning picks up their own thread —
  // the nickname is what keys their guest row.
  const [nickname, setNickname] = useState(() => {
    try { return localStorage.getItem('ace.guest.nickname') || ''; } catch { return ''; }
  });
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  const submitAccessCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccessError(null);
    if (!accessCode.trim()) {
      setAccessError('Enter the access code.');
      return;
    }
    setAccessBusy(true);
    try {
      try { localStorage.setItem('ace.guest.nickname', nickname.trim()); } catch { /* private window */ }
      const { token, user } = await accessCodeLogin(accessCode.trim(), nickname.trim());
      setAuth(token, user);
      // Navigate EXPLICITLY, exactly as the student-ID and staff paths do.
      // The "already signed in?" effect below cannot carry this: its only
      // dependency is `isAuthenticated`, which is a stable function reference
      // on the store, so the effect runs on mount and never again. Setting the
      // token does not re-trigger it. Without this line the code is accepted,
      // the session is real, and the page just sits there.
      navigate('/', { replace: true });
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAccessError(msg || 'Could not sign in with that code.');
    } finally {
      setAccessBusy(false);
    }
  };

  // Never sit on a tab that is not being offered — that renders an empty panel
  // under a tab strip that does not include it. Staff is the fallback because
  // it is the one way in that is always available.
  useEffect(() => {
    if (audience === 'student' && !studentLoginOffered) {
      setAudience(accessCodeOffered ? 'access' : 'staff');
    } else if (audience === 'access' && !accessCodeOffered) {
      setAudience(studentLoginOffered ? 'student' : 'staff');
    }
  }, [audience, studentLoginOffered, accessCodeOffered]);

  // Already signed in? Skip the login screen.
  useEffect(() => {
    if (isAuthenticated()) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
  };

  const handleStudentLogin = async (e: FormEvent) => {
    e.preventDefault();
    setStudentError(null);

    if (!/^\d{9}$/.test(studentId.trim())) {
      setStudentError('Student ID must be your 9-digit college ID.');
      return;
    }
    if (!studentPassword) {
      setStudentError('Please enter your password.');
      return;
    }

    setStudentBusy(true);
    try {
      const { token, user, must_change_password } = await studentLogin(
        studentId.trim(),
        studentPassword,
        getDeviceId()
      );
      setAuth(token, user);
      navigate(must_change_password ? '/account/password?forced=1' : '/', { replace: true });
    } catch (err) {
      setStudentError(messageForStudentLoginError(err));
    } finally {
      setStudentBusy(false);
    }
  };

  const handleEmailPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === 'register' && !name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setBusy(true);
    try {
      const { token, user } =
        mode === 'signin'
          ? await login(email.trim(), password)
          : await register(email.trim(), name.trim(), password);
      setAuth(token, user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(messageForAuthError(err, mode));
    } finally {
      setBusy(false);
    }
  };

  const handleMicrosoft = async () => {
    if (!isMsalConfigured()) {
      setError('Microsoft sign-in is not configured (missing VITE_MSAL_CLIENT_ID).');
      return;
    }
    setOauthBusy(true);
    setError(null);
    try {
      await msalInstance.initialize();
      // Redirect flow — the browser leaves for login.microsoftonline.com and
      // returns to /auth/microsoft/callback, where the token exchange happens.
      await msalInstance.loginRedirect(loginRequest);
    } catch {
      setOauthBusy(false);
      setError('Could not start Microsoft sign-in. Please try again.');
    }
  };

  const handleGoogle = () => {
    // Server-side redirect flow.
    window.location.assign(googleLoginUrl());
  };

  const inputClass =
    'w-full rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-ink placeholder:text-ws-ink-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent';

  return (
    <div className="min-h-screen grid place-items-center bg-ws-bg px-4 py-8">
      {/* Two-column premium auth card: form (left) + branding aside (right). */}
      <div className="grid w-full max-w-5xl overflow-hidden rounded-2xl border border-ws-tile-border bg-ws-tile shadow-lg lg:grid-cols-2">
        {/* ── LEFT: the auth form ───────────────────────────────────────── */}
        <div className="flex flex-col justify-center p-8 sm:p-10 lg:p-12">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-7 flex flex-col items-start text-left">
              <span className="size-11 rounded-lg bg-ws-accent text-white grid place-items-center text-xl font-extrabold mb-4">
                A
              </span>
              <h1 className="text-2xl font-semibold tracking-tight text-ws-ink">
                {audience === 'student' ? 'Sign in' : mode === 'signin' ? 'Welcome back' : 'Create your account'}
              </h1>
              <p className="text-sm text-ws-ink-3 mt-1.5">
                {audience === 'student'
                  ? 'Sign in to continue to ACE Core Chemistry.'
                  : mode === 'signin'
                    ? 'Sign in to continue to ACE Core Chemistry.'
                    : 'Start learning with narrated lectures and virtual labs.'}
              </p>
            </div>

            {/* Audience switcher — Student ID first (BP 26): most sign-ins are
                students, and @dcmail.ca cannot use Microsoft sign-in at all. */}
            <div
              role="tablist"
              aria-label="Sign-in method"
              className="mb-5 inline-flex rounded-lg border border-ws-border bg-ws-surface p-0.5"
            >
              {studentLoginOffered && (
              <button
                type="button"
                role="tab"
                aria-selected={audience === 'student'}
                onClick={() => {
                  setAudience('student');
                  setError(null);
                }}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent ${
                  audience === 'student' ? 'bg-ws-accent text-white' : 'text-ws-ink-2 hover:text-ws-ink'
                }`}
              >
                Student ID
              </button>
              )}
              <button
                type="button"
                role="tab"
                aria-selected={audience === 'staff'}
                onClick={() => {
                  setAudience('staff');
                  setStudentError(null);
                }}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent ${
                  audience === 'staff' ? 'bg-ws-accent text-white' : 'text-ws-ink-2 hover:text-ws-ink'
                }`}
              >
                Staff
              </button>
              {accessCodeOffered && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={audience === 'access'}
                  onClick={() => {
                    setAudience('access');
                    setError(null);
                    setStudentError(null);
                  }}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent ${
                    audience === 'access' ? 'bg-ws-accent text-white' : 'text-ws-ink-2 hover:text-ws-ink'
                  }`}
                >
                  Access code
                </button>
              )}
            </div>

            {audience === 'access' ? (
              <form onSubmit={submitAccessCode} className="space-y-4">
                <p className="text-sm text-ws-ink-2">
                  Your instructor will give you a code. It lets you read the course
                  without an account.
                </p>
                {accessError && (
                  <div role="alert" className="rounded-md border-l-[3px] border-ws-danger bg-ws-danger/10 px-3 py-2 text-sm text-ws-ink">
                    {accessError}
                  </div>
                )}
                <label className="block">
                  <span className="text-sm font-medium text-ws-ink-2">Access code</span>
                  {/* No character filtering: a code may be letters, digits or
                      both. The default is digits only because it is built from
                      atomic numbers, not because the field is numeric. */}
                  <input
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    autoComplete="off"
                    autoFocus
                    placeholder="access code"
                    aria-label="Access code"
                    inputMode="text"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={32}
                    className="mt-1 w-full rounded-lg border-2 border-ws-border bg-ws-surface px-4 py-3 text-center font-mono text-2xl tracking-[0.2em] text-ws-ink focus:border-ws-accent focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ws-ink-2">
                    Nickname <span className="font-normal text-ws-ink-4">&mdash; optional</span>
                  </span>
                  <input
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value.slice(0, 24))}
                    autoComplete="off"
                    autoCapitalize="words"
                    spellCheck={false}
                    maxLength={24}
                    placeholder="e.g. Chem Fox"
                    aria-label="Nickname"
                    aria-describedby="nick-hint"
                    className="mt-1 w-full rounded-lg border-2 border-ws-border bg-ws-surface px-4 py-3 text-lg text-ws-ink focus:border-ws-accent focus:outline-none"
                  />
                  <span id="nick-hint" className="mt-1 block text-xs text-ws-ink-3">
                    So you can join class activities and your answers stay yours. Not your
                    real name &mdash; anything you like. Leave it blank and you still get in.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={accessBusy || !accessCode.trim()}
                  className="w-full rounded-lg bg-ws-accent px-4 py-3 text-base font-semibold text-ws-on-accent transition hover:bg-ws-accent-hover disabled:opacity-40"
                >
                  {accessBusy ? 'Checking…' : 'Continue'}
                </button>
                {/* Said before they are in, not after they hit a wall.
                    It points at /a, NOT at student sign-in: sign-in is off
                    while the college has not cleared accounts, so telling a
                    student to "sign in with your student ID" for attendance
                    would send them to a door that is locked. */}
                <p className="rounded-md border-l-[3px] border-ws-warning bg-ws-warning/10 px-3 py-2 text-xs text-ws-ink">
                  <b>You can join class activities</b> with this code, and your answers stay
                  under your nickname. Nothing else is saved to a student record, and{' '}
                  <b>it does not mark you present</b>. To be counted in class, check in at{' '}
                  {/* Relative href so it also works on the Railway fallback
                      host; the label names the canonical domain. */}
                  <a
                    href="/a"
                    className="font-semibold text-ws-accent underline underline-offset-2 hover:text-ws-accent-hover"
                  >
                    chemistryq.com/a
                  </a>.
                </p>
              </form>
            ) : audience === 'student' ? (
              <>
                {studentError && (
                  <div
                    id="student-login-error"
                    role="alert"
                    aria-invalid="true"
                    className="mb-4 rounded-md border border-ws-danger bg-ws-surface-2 px-3 py-2 text-sm text-ws-danger"
                  >
                    {studentError}
                  </div>
                )}

                <form onSubmit={handleStudentLogin} className="flex flex-col gap-2.5">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-ws-ink-2">Student ID</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="username"
                      value={studentId}
                      onChange={(e) => setStudentId(e.target.value.replace(/[^\d]/g, '').slice(0, 9))}
                      placeholder="123456789"
                      maxLength={9}
                      aria-invalid={!!studentError}
                      aria-describedby={studentError ? 'student-login-error' : undefined}
                      className={inputClass}
                      required
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-ws-ink-2">Password</span>
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={studentPassword}
                      onChange={(e) => setStudentPassword(e.target.value)}
                      placeholder="Your password"
                      aria-invalid={!!studentError}
                      aria-describedby={studentError ? 'student-login-error' : undefined}
                      className={inputClass}
                      required
                    />
                  </label>

                  <Button type="submit" size="lg" loading={studentBusy} className="w-full mt-1">
                    Sign in
                  </Button>
                </form>

                <p className="mt-4 text-center text-sm text-ws-ink-3">
                  <a href="/reset" className="font-medium text-ws-accent hover:text-ws-accent-ink underline">
                    Forgot your password?
                  </a>
                </p>

                <p className="mt-6 text-xs text-ws-ink-4">
                  Students: use your 9-digit student ID and the password you were given. College
                  email does not work with Microsoft sign-in.
                </p>
              </>
            ) : (
              <>
                {error && (
                  <div
                    id="staff-login-error"
                    role="alert"
                    aria-invalid="true"
                    className="mb-4 rounded-md border border-ws-danger bg-ws-surface-2 px-3 py-2 text-sm text-ws-danger"
                  >
                    {error}
                  </div>
                )}

                {/* OAuth providers */}
                <div className="flex flex-col gap-2.5">
              <Button
                variant="secondary"
                size="lg"
                onClick={handleMicrosoft}
                loading={oauthBusy}
                className="w-full"
              >
                <MsIcon /> Continue with Microsoft
              </Button>
              <Button variant="secondary" size="lg" onClick={handleGoogle} className="w-full">
                <GoogleIcon /> Continue with Google
              </Button>
            </div>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-ws-border" />
              <span className="text-xs text-ws-ink-4">or</span>
              <span className="h-px flex-1 bg-ws-border" />
            </div>

            {/* Email / password form */}
            <form onSubmit={handleEmailPassword} className="flex flex-col gap-2.5">
              {mode === 'register' && (
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-ws-ink-2">Name</span>
                  <input
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    aria-invalid={!!error}
                    aria-describedby={error ? 'staff-login-error' : undefined}
                    className={inputClass}
                    required
                  />
                </label>
              )}

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ws-ink-2">Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'staff-login-error' : undefined}
                  className={inputClass}
                  required
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-ws-ink-2">Password</span>
                <input
                  type="password"
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'At least 8 characters' : 'Your password'}
                  minLength={8}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'staff-login-error' : undefined}
                  className={inputClass}
                  required
                />
              </label>

              <Button type="submit" size="lg" loading={busy} className="w-full mt-1">
                {mode === 'signin' ? 'Sign in' : 'Create account'}
              </Button>
            </form>

            {/* Mode toggle */}
            <p className="mt-4 text-center text-sm text-ws-ink-3">
              {mode === 'signin' ? (
                <>
                  New here?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('register')}
                    className="font-medium text-ws-accent hover:text-ws-accent-ink underline"
                  >
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode('signin')}
                    className="font-medium text-ws-accent hover:text-ws-accent-ink underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </p>

                <p className="mt-6 text-center text-xs text-ws-ink-4">
                  By continuing you agree to the course terms of use.
                </p>
              </>
            )}
          </div>
        </div>

        {/* ── RIGHT: branding aside (hidden < lg) ───────────────────────── */}
        <aside
          aria-hidden="true"
          className="relative hidden flex-col justify-between overflow-hidden p-12 text-white lg:flex"
          style={{
            background:
              'radial-gradient(120% 120% at 85% 15%, color-mix(in oklab, var(--ws-accent) 70%, white) 0%, transparent 55%), linear-gradient(150deg, var(--ws-accent) 0%, var(--ws-dark-teal) 100%)',
          }}
        >
          <div className="relative z-10">
            <div className="flex items-center gap-2.5">
              <span className="grid size-9 place-items-center rounded-lg bg-white/15 text-lg font-extrabold backdrop-blur-sm">
                A
              </span>
              <span className="text-lg font-semibold tracking-tight">ACE Core Chemistry</span>
            </div>
            <h2 className="mt-10 text-3xl font-semibold leading-tight tracking-tight">
              Chemistry that
              <br />
              actually clicks.
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/80">
              A guided course built around narrated lessons, hands-on labs, and an AI tutor that
              answers when you get stuck.
            </p>
          </div>

          <ul className="relative z-10 mt-10 flex flex-col gap-4 text-sm">
            {[
              'Narrated interactive lectures',
              'Spaced-repetition flashcards',
              'Virtual labs you can run in the browser',
              'AI tutor & Q&A, on demand',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-white/90">
                <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-white/20">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="m5 12 5 5L20 7" />
                  </svg>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <p className="relative z-10 mt-10 text-sm italic leading-relaxed text-white/75">
            "Finally a chemistry course that explains the why, not just the what."
          </p>
        </aside>
      </div>
    </div>
  );
}
